import PropTypes from 'prop-types';
import React from 'react';
import clsx from 'clsx';

import {
    Image,
    Link,
} from 'reaxl';

import { trimString } from 'atc-js';
import { imageSizes } from '@atc/images';
import { useDevice } from '@atc/react-device';

/**
 *  News & Reviews component will be used to feature an article and list an additional 4 supporting sub-articles
 *
 */
function NewsReviews({
    articles = [],
    className,
    collapsed = false,
    onClick = () => { },
    ...rest
}) {
    const { mediaType } = useDevice() || {};
    let featureImageSize;
    let subImageSize;

    switch (mediaType) {
        case 'xs':
            featureImageSize = imageSizes.newsReviewsFeatureImgXs;
            subImageSize = imageSizes.newsReviewsSubImgXs;
            break;
        case 'sm':
            featureImageSize = imageSizes.newsReviewsFeatureImgSm;
            subImageSize = imageSizes.newsReviewsSubImgSm;
            break;
        case 'md':
            featureImageSize = imageSizes.newsReviewsFeatureImgMd;
            subImageSize = imageSizes.newsReviewsSubImgMd;
            break;
        case 'lg':
            featureImageSize = imageSizes.newsReviewsFeatureImgLg;
            subImageSize = imageSizes.newsReviewsSubImgLg;
            break;
        default:
            featureImageSize = imageSizes.newsReviewsFeatureImgLg;
            subImageSize = imageSizes.newsReviewsSubImgLg;
    }

    const renderPromotedLogo = (article) => (
        <Link
            label={(
                <Image
                    alt="sponsor logo"
                    className="img img-responsive padding-vertical-1"
                    width={50}
                    lazyload
                    src={article.promotedLogoUrl}
                />
            )}
            href={article.href}
            onClick={(e) => onClick(e, article)}
        />
    );

    const renderFeaturedArticle = () => {

        const featuredArticle = Array.isArray(articles) && articles.length > 0 && articles[0];

        if (featuredArticle) {

            const {
                promoted,
                promotedLogoUrl,
                title,
                text,
                src,
                href,
            } = featuredArticle;

            const featuredArticleWrapper = clsx({
                'featuredArticle-text': true,
                'bg-neutral-secondary': !collapsed,
            });

            const featuredTitle = promoted ? 'Promoted' : 'Featured';

            return (

                <div className="padding-0 padding-left-sm-2 padding-right-sm-2 display-flex">
                    <div className="col-xs-3 col-sm-6 padding-0">
                        <Link
                            label={(
                                <div
                                    style={{ minHeight: featureImageSize.height }}
                                >
                                    <Image
                                        src={src}
                                        className="bg-neutral-secondary col-xs-12 padding-0"
                                        lazyload
                                        style={{
                                            '--aspect-ratio-calculation': `${100 / (featureImageSize.width / featureImageSize.height)}%`,
                                            width: '100%',
                                            maxWidth: '100%',
                                        }}
                                        aria-hidden="true"
                                    />
                                </div>
                            )}
                            className="featured-image"
                            href={href}
                            aria-label={title}
                            onClick={(e) => onClick(e, featuredArticle, { referenceData: { articleCategory: featuredTitle, referenceType: 'image' } })}
                        />
                    </div>
                    <div className={`col-xs-9 col-sm-6 display-flex justify-content ${featuredArticleWrapper}`}>
                        <div className="col-xs-11 padding-verticle-sm-5 display-flex justify-content-sm-center flex-column padding-left-sm-5 padding-left-0">
                            <p className="text-uppercase text-gray text-size-200 margin-bottom-0">
                                {featuredTitle}
                            </p>
                            <span className="text-gray-base text-size-sm-500">
                                <Link
                                    label={title}
                                    href={href}
                                    className="text-gray-base"
                                    onClick={(e) => onClick(e, featuredArticle, { referenceData: { articleCategory: featuredTitle } })}
                                    undecorated
                                />
                            </span>
                            <p
                                className="text-gray-dark padding-top-4 display-none display-sm-block"
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{ __html: trimString(text, collapsed ? 100 : 150) }}
                            />
                        </div>
                        <div>
                            {promoted && (
                                <Image
                                    alt="promoted logo"
                                    width={60}
                                    lazyload
                                    src={promotedLogoUrl}
                                />
                            )}
                        </div>
                    </div>
                </div>
            );
        }
        return null;

    };

    const renderSubArticles = () => {

        const subArticles = Array.isArray(articles) && articles.length > 1 && articles.slice(1);

        if (subArticles) {

            const subArticleList = subArticles.map((article, idx) => {

                const promoted = article.promoted || false;
                const articleType = promoted ? 'Promoted' : article.type;

                const subLinkClasses = clsx({
                    'text-gray-base': true,
                    'col-xs-9 padding-horizontal-0': promoted && collapsed,
                    'margin-right-2': collapsed,
                });

                return (
                    <div
                        className="col-xs-12 col-sm-3 padding-2 display-flex flex-row flex-sm-column"
                        key={idx}
                    >
                        <div className="col-sm-12 col-xs-3 padding-0 bg-neutral-primary">
                            <Link
                                label={(
                                    <div
                                        style={{ minHeight: subImageSize.height }}
                                    >
                                        <Image
                                            className="col-xs-12 padding-0"
                                            lazyload
                                            src={article.src}
                                            style={{
                                                '--aspect-ratio-calculation': `${100 / (subImageSize.width / subImageSize.height)}%`,
                                                width: '100%',
                                                maxWidth: '100%',
                                            }}
                                            aria-hidden="true"
                                        />
                                    </div>
                                )}
                                href={article.href}
                                aria-label={article.title}
                                onClick={(e) => onClick(e, article, { referenceData: { articleCategory: articleType, referenceType: 'image' } })}
                            />
                        </div>
                        <div className="col-xs-9 col-sm-12 margin-top-sm-3 padding-left-2 padding-left-sm-0">
                            <p className="text-uppercase text-gray text-size-200 margin-bottom-0">
                                {articleType}
                            </p>
                            <div className="display-flex justify-content-between">
                                <Link
                                    label={article.title}
                                    className={subLinkClasses}
                                    href={article.href}
                                    onClick={(e) => onClick(e, article, { referenceData: { articleCategory: articleType } })}
                                />
                                {promoted && collapsed && renderPromotedLogo(article)}
                            </div>
                            {promoted && !collapsed && renderPromotedLogo(article)}
                        </div>
                    </div>
                );
            });

            return (
                <div className="col-sm-12 padding-0 display-flex flex-column flex-sm-row">
                    {subArticleList}
                </div>
            );
        }
        return null;

    };

    return (
        <div
            data-cmp="newReviews"
            className={className}
            {...rest}
        >
            <div className="row padding-2 padding-sm-0 margin-top-sm-4">
                {renderFeaturedArticle()}
            </div>
            <div className="row margin-top-sm-2">
                {renderSubArticles()}
            </div>
        </div>
    );

}

NewsReviews.propTypes = {
    /**
    * The className to apply to the root node
    */
    className: PropTypes.string,
    /**
     * If true, the component will render in collapsed mode
     */
    collapsed: PropTypes.bool,
    /**
     * An array of articles where the first item represents the featured article
     */
    articles: PropTypes.array,
    /**
     * Callback function fired when article image and title is clicked)
     */
    onClick: PropTypes.func,
};

export default NewsReviews;
